/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */

import { enrichmentBFF } from 'Utils/httpClients';
import { KueskiAttribution } from '@kueski-dev/web-observability';

import { DOMAIN, LoggerFactory } from 'Services/Logger';
import { GTMSession } from './GTMSession';

const logger = LoggerFactory.getLogger(DOMAIN.FRONT);

export const GTM_CLIENT_ID_KEY = 'GTM_CLIENT_ID';
export const GTM_SESSION_ID_KEY = 'GTM_SESSION_ID';
export const GTM_DATA_ID = 'GTM_DATA_ID';

type UpdateOverrides = Partial<KueskiAttribution>;

export class GTMSessionState {
  public readonly session: GTMSession;

  private readonly clientIdCacheKey: string;

  private readonly sessionIdCacheKey: string;

  private readonly dataCacheKey: string;

  private get dataCache(): Record<string, string | null | undefined> {
    const rawValue = localStorage.getItem(this.dataCacheKey);

    if (!rawValue) return {};

    try {
      return JSON.parse(rawValue);
    } catch {
      return {};
    }
  }

  private set dataCache(value: Record<string, string | null | undefined>) {
    if (value) localStorage.setItem(this.dataCacheKey, JSON.stringify(value));
  }

  private get clientId(): string | undefined {
    return localStorage.getItem(this.clientIdCacheKey) ?? undefined;
  }

  private set clientId(value: string | undefined) {
    if (value) localStorage.setItem(this.clientIdCacheKey, value);
  }

  private get sessionId(): string | undefined {
    return localStorage.getItem(this.sessionIdCacheKey) ?? undefined;
  }

  private set sessionId(value: string | undefined) {
    if (value) localStorage.setItem(this.sessionIdCacheKey, value);
  }

  constructor(measurementId: string) {
    this.session = new GTMSession(measurementId);
    this.clientIdCacheKey = `${GTM_CLIENT_ID_KEY}_${measurementId}`;
    this.sessionIdCacheKey = `${GTM_SESSION_ID_KEY}_${measurementId}`;
    this.dataCacheKey = `${GTM_DATA_ID}_${measurementId}`;
  }

  public async safeUpdate(userId: string, overrides: UpdateOverrides = {}): Promise<boolean> {
    const dataDelta = this.dataDelta(overrides);
    if (!this.isChanged() && !dataDelta) return false;
    return this.updateEnrichmentContext(userId, dataDelta);
  }

  public async forceUpdate(userId: string, overrides: UpdateOverrides = {}) {
    const dataDelta = this.dataDelta(overrides);
    return this.updateEnrichmentContext(userId, dataDelta);
  }

  public clear(): void {
    localStorage.removeItem(this.clientIdCacheKey);
    localStorage.removeItem(this.sessionIdCacheKey);
    localStorage.removeItem(this.dataCacheKey);
  }

  private refresh(): void {
    this.clientId = this.session.clientId;
    this.sessionId = this.session.sessionId;
  }

  private findGTMId(value?: string): string | undefined {
    if (!value) return undefined;
    return value.split('.').at(2);
  }

  private dataDelta(dataOverride: UpdateOverrides = {}) {
    const dataChanged = Object
      .entries(dataOverride)
      .filter(([key, value]) => {
        if (!value) return false;
        const storedValue = this.dataCache[key];
        return value !== storedValue;
      });

    if (!dataChanged.length) return undefined;

    return Object.fromEntries(dataChanged) as UpdateOverrides;
  }

  private isChanged(): boolean {
    const currentClientId = this.findGTMId(this.clientId);
    const currentSessionId = this.findGTMId(this.sessionId);
    const gtmClientId = this.findGTMId(this.session.clientId);
    const gtmSessionId = this.findGTMId(this.session.sessionId);

    return (
      currentClientId !== gtmClientId
      || currentSessionId !== gtmSessionId
    );
  }

  private async updateEnrichmentContext(userId: string, overrides: UpdateOverrides = {}) {
    const oldClientId = this.clientId;
    const oldSessionId = this.sessionId;

    this.refresh(); // Lock the current values to avoid multiple updates

    const currentUrl = new URL(window.location.href);

    const relevantData = {
      ...this.dataCache,
      ...overrides,
    };

    const data = {
      container_product_id: 'pay',
      fbclid: currentUrl.searchParams.get('fbclid'),
      ga_client_id: this.session.clientId,
      ga_session_id: this.session.sessionId,
      gclid: currentUrl.searchParams.get('gclid'),
      user_id: userId,
      utm_campaign: currentUrl.searchParams.get('utm_campaign'),
      utm_content: currentUrl.searchParams.get('utm_content'),
      utm_medium: currentUrl.searchParams.get('utm_medium'),
      utm_source: currentUrl.searchParams.get('utm_source'),
      utm_term: currentUrl.searchParams.get('utm_term'),
      ...relevantData,
    };

    const debugData = {
      ...data,
      old_ga_client_id: oldClientId,
      old_ga_session_id: oldSessionId,
      url: currentUrl.href,
    };

    const prevData = { ...this.dataCache };

    try {
      this.dataCache = { ...relevantData };
      await enrichmentBFF.createContext(data);
      logger.info(
        'Context created',
        DOMAIN.FRONT,
        debugData,
      );
      return true;
    } catch (error) {
      this.clear();
      this.dataCache = prevData;
      logger.error(
        'Error creating context',
        DOMAIN.FRONT,
        {
          ...debugData,
          error,
        },
      );
      return false;
    }
  }
}

export const payGTMSessionState = new GTMSessionState('L84DN1ME82');
